import { z } from "zod"

/**
 * Validation schemas for user input
 * Prevents malicious input and enforces data integrity
 */

// Book/Movie item validation
export const BookMovieItemSchema = z.object({
  title: z.string().min(1, "Title cannot be empty").max(500, "Title must be 500 characters or less").trim(),
  author: z.string().max(200, "Author name must be 200 characters or less").trim().optional().or(z.literal("")),
  year: z
    .string()
    .max(4, "Year must be 4 characters or less")
    .regex(/^\d{1,4}$/, "Year must be numeric")
    .optional()
    .or(z.literal("")),
  completed: z.boolean().default(false),
  rating: z.number().int().min(1).max(5).optional(),
})

export type BookMovieItemInput = z.infer<typeof BookMovieItemSchema>

// Partial book/movie item validation (for updates)
export const BookMovieItemPartialSchema = BookMovieItemSchema.partial()

export type BookMovieItemPartialInput = z.infer<typeof BookMovieItemPartialSchema>

// Date night idea validation
export const DateNightIdeaSchema = z.object({
  idea: z.string().min(1, "Idea cannot be empty").max(500, "Idea must be 500 characters or less").trim(),
  completed: z.boolean().default(false),
})

export type DateNightIdeaInput = z.infer<typeof DateNightIdeaSchema>

// Partial date night idea validation (for updates)
export const DateNightIdeaPartialSchema = DateNightIdeaSchema.partial()

export type DateNightIdeaPartialInput = z.infer<typeof DateNightIdeaPartialSchema>

// Planner content field validation (for individual fields)
export const PlannerFieldSchema = z.object({
  fieldId: z.string().min(1).max(100).regex(/^[a-zA-Z0-9_-]+$/, "Field ID must be alphanumeric with dashes/underscores"),
  value: z.string().max(50000, "Content must be 50,000 characters or less"), // Reasonable limit for rich text content
})

export type PlannerFieldInput = z.infer<typeof PlannerFieldSchema>

// Product ID validation
export const ProductIdSchema = z.enum(["creator-monthly", "dreamer-monthly"])

// Email validation - blocks suspicious/incomplete emails
// Validates proper email format and blocks common test/example patterns
export const EmailSchema = z
  .string()
  .email("Please enter a valid email address")
  .min(5, "Email address is too short")
  .max(254, "Email address is too long")
  .refine(
    (email) => {
      const [localPart, domain] = email.split("@")
      
      // Block incomplete emails (missing domain)
      if (!domain || domain.length === 0) {
        return false
      }
      
      // Block suspicious patterns in local part
      const suspiciousLocalParts = ["example", "test", "temp", "fake", "dummy", "admin", "user"]
      if (suspiciousLocalParts.includes(localPart.toLowerCase())) {
        return false
      }
      
      // Require domain to have at least one dot (e.g., example.com)
      if (!domain.includes(".")) {
        return false
      }
      
      // Block suspicious domains
      const suspiciousDomains = ["example.com", "test.com", "temp.com", "fake.com", "dummy.com"]
      if (suspiciousDomains.includes(domain.toLowerCase())) {
        return false
      }
      
      // Require valid TLD (at least 2 characters after last dot)
      const parts = domain.split(".")
      const tld = parts[parts.length - 1]
      if (!tld || tld.length < 2) {
        return false
      }
      
      // Local part must be at least 1 character
      if (!localPart || localPart.length === 0) {
        return false
      }
      
      return true
    },
    {
      message: "Please enter a valid email address. Test or example emails are not allowed.",
    }
  )

// Stripe session ID validation
export const StripeSessionIdSchema = z.string().regex(/^cs_(test|live)_[a-zA-Z0-9]{24,}$/, "Invalid session ID format")

// User preference validation
export const ColorPaletteSchema = z.object({
  primary: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Primary color must be a valid hex color"),
  secondary: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Secondary color must be a valid hex color").optional(),
  accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Accent color must be a valid hex color").optional(),
  neutral: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Neutral color must be a valid hex color").optional(),
})

export const UserPreferencesSchema = z.object({
  color_palette: ColorPaletteSchema,
  selected_template: z.string().max(50).trim().optional(), // Legacy field, kept for backward compatibility
  selected_templates: z.array(z.string().max(50).trim()).min(1).optional(), // New array field
  dreamer_template_tour_seen_at: z.string().datetime().nullable().optional(),
  last_used_decorations: z
    .object({
      sticker: z.string().max(100).optional(),
      tape: z.string().max(100).optional(),
    })
    .optional(),
})

/**
 * Validates and sanitizes book/movie item input (full item)
 */
export function validateBookMovieItem(input: unknown): BookMovieItemInput {
  return BookMovieItemSchema.parse(input)
}

/**
 * Validates partial book/movie item input (for updates)
 */
export function validateBookMovieItemPartial(input: unknown): BookMovieItemPartialInput {
  return BookMovieItemPartialSchema.parse(input)
}

/**
 * Validates planner field input
 */
export function validatePlannerField(input: unknown): PlannerFieldInput {
  return PlannerFieldSchema.parse(input)
}

/**
 * Validates and sanitizes date night idea input (full item)
 */
export function validateDateNightIdea(input: unknown): DateNightIdeaInput {
  return DateNightIdeaSchema.parse(input)
}

/**
 * Validates partial date night idea input (for updates)
 */
export function validateDateNightIdeaPartial(input: unknown): DateNightIdeaPartialInput {
  return DateNightIdeaPartialSchema.parse(input)
}

/**
 * Validates product ID
 */
export function validateProductId(productId: string): "creator-monthly" | "dreamer-monthly" {
  return ProductIdSchema.parse(productId)
}

/**
 * Validates Stripe session ID
 */
export function validateStripeSessionId(sessionId: string): string {
  return StripeSessionIdSchema.parse(sessionId)
}

/**
 * Validates email address
 * Blocks suspicious/incomplete emails like "example@", "test@", etc.
 */
export function validateEmail(email: string): string {
  return EmailSchema.parse(email)
}

